// Administration → Activité: what the members ask and do, team by team (GET /api/admin/activity,
// newest first, ACTIVITY_PAGE per page): their requests (tasks of the last 30 days), their
// messages to the agents when the server lets administrators read the discussions
// (OFFICE_ADMIN_CHAT_VIEW: the response's `chats`) and their recent actions from the audit log.
// Filters by person, floor, service and type (remembered for the browser tab), « AFFICHER PLUS »
// pages with `before`, relative times refreshed every minute, the first page reloaded after task
// events. Read-only: a request opens its task drawer (#/task/<id>?from=activite, which comes back
// here when closed), a message opens the discussion in a read-only drawer (no composer, nothing to
// clear, no task to launch). The users tab opens it on one person (`params.userId`).
import { h, button, select, badge, emptyState, icon, infoTip, stateBadge } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime, renderMarkdown, toDate, initials, hashHue } from '../../format.js';
import { openLightbox } from '../tasks/components.js';
import { thumb } from '../attachments.js';
import { agentAvatar } from '../agent/avatar.js';
import { auditActionLabel, auditTone } from './audit-format.js';
import {
  WHO_MEMBERS, activityQuery, normalizeActivity, mergeItems, teamLabel, personOptions, floorChoices,
  deptChoices, kindChoices, cleanFilters, groupByDay, fromLabel, knownState, chatTitle, actionDetails, adminChatPath,
  adminChatFileUrl, normalizeConversation,
} from './activity-model.js';
import { sectionHead, loadingState, errorState } from './common.js';
import './strings.js';

/** Wait after a filter change before asking the server (several changes → one request). */
export const ACTIVITY_DEBOUNCE_MS = 150;
/** Wait after a task event before reloading the first page (a burst → one request). */
export const ACTIVITY_LIVE_MS = 1500;
/** sessionStorage key of the filters (they survive the round trip through a task drawer). */
const MEMORY_KEY = 'mo.adm.activity';

function readMemory() {
  try {
    const raw = globalThis.sessionStorage?.getItem(MEMORY_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

function writeMemory(value) {
  try { globalThis.sessionStorage?.setItem(MEMORY_KEY, JSON.stringify(value)); } catch { /* storage refused */ }
}

/** A <time> with its relative text (refreshed by the view's timer) and the exact date as a tooltip. */
function timeEl(at, now) {
  const d = toDate(at);
  return d ? h('time', { class: 'adm-act-when', datetime: d.toISOString(), title: formatDateTime(d, now) }, relativeTime(d, now)) : null;
}

/**
 * Creates the « Activité » tab.
 * @param {object} ctx app context
 * @param {{params?: {userId?: string, userName?: string}|null}} [nav] the admin sheet's navigation
 *   (params: open on one person)
 * @returns {{el: HTMLElement, reload(): Promise<void>, applyParams(p: object): void, destroy(): void}}
 */
export function createActivityView(ctx, { params = null } = {}) {
  const office = () => ctx.store.get().office || {};
  let items = null;          // the items shown (every page loaded)
  let itemsKey = null;       // the filters they were loaded for (filterKey())
  let nextBefore = null;     // cursor of the next (older) page, null at the end
  let pages = 0;
  let firstPending = false;  // a first page is due (filters changed) or on its way
  let chats = false;         // the server lets administrators read the discussions
  let chatsKnown = false;    // a response said so (until then a remembered « Discussions » stays)
  let tipFor = null;         // the `chats` value the « ? » was built for
  let error = null;
  let users = [];
  let seq = 0;
  let loadingMore = false;
  let destroyed = false;
  let filterTimer = null;
  let liveTimer = null;
  const memory = readMemory() || {};
  let filters = cleanFilters(memory, office());
  let personName = typeof memory.personName === 'string' ? memory.personName : '';
  if (params?.userId) {
    filters = { ...filters, person: String(params.userId), floorId: '', deptId: '' };
    personName = String(params.userName || '');
  }

  const refreshBtn = button(t('adm.refresh'), { variant: 'secondary', icon: 'refresh', onClick: () => load() });
  const tipSlot = h('span', { class: 'adm-act-tip' });
  const head = sectionHead({ title: t('adm.activity.title'), intro: [t('adm.activity.intro'), ' ', tipSlot], actions: [refreshBtn] });
  const personSel = select([], { class: 'input-sm', 'aria-label': t('adm.activity.person') });
  const floorSel = select([], { class: 'input-sm', 'aria-label': t('adm.activity.floor') });
  const deptSel = select([], { class: 'input-sm', 'aria-label': t('adm.activity.dept') });
  const kindSel = select([], { class: 'input-sm', 'aria-label': t('adm.activity.kind') });
  personSel.addEventListener('change', () => {
    filters.person = personSel.value;
    personName = personSel.value === WHO_MEMBERS ? '' : [...personSel.childNodes].find((o) => o.value === personSel.value)?.textContent || '';
    changed();
  });
  floorSel.addEventListener('change', () => { filters.floorId = floorSel.value; filters.deptId = ''; renderFilters(); changed(); });
  deptSel.addEventListener('change', () => { filters.deptId = deptSel.value; changed(); });
  kindSel.addEventListener('change', () => { filters.kind = kindSel.value; changed(); });
  const label = (text, control, cls) => h('label', { class: ['adm-filter', cls] }, h('span', { class: 'mono-label' }, text), control);
  const filterBar = h('div', { class: 'adm-audit-filters adm-act-filters', role: 'search' },
    label(t('adm.activity.person'), personSel, 'adm-act-filter-person'),
    label(t('adm.activity.floor'), floorSel),
    label(t('adm.activity.dept'), deptSel),
    label(t('adm.activity.kind'), kindSel));
  const moreBtn = button(t('adm.activity.more'), { variant: 'secondary', class: 'adm-more adm-act-more', onClick: () => load({ more: true }) });
  const body = h('div', { class: 'adm-body adm-act-feed' }, loadingState());
  const el = h('section', { class: 'adm-view adm-activity' }, head, filterBar, body);

  function remember() {
    writeMemory({ ...filters, personName });
  }

  /** The filters as the server gets them (the query without its cursor): a list belongs to one key. */
  const filterKey = () => JSON.stringify(activityQuery(filters));

  /** A filter changed: back to the first page, shortly (several changes make one request). */
  function changed() {
    remember();
    clearTimeout(filterTimer);
    firstPending = true;
    moreBtn.disabled = true;
    body.setAttribute('aria-busy', 'true');
    filterTimer = setTimeout(() => { filterTimer = null; load(); }, ACTIVITY_DEBOUNCE_MS);
  }

  function fillSelect(sel, options, value) {
    sel.replaceChildren(...options.map((o) => h('option', { value: o.value }, o.label)));
    sel.value = options.some((o) => o.value === value) ? value : options[0].value;
    return sel.value;
  }

  function renderFilters() {
    filters.person = fillSelect(personSel, personOptions(users, { current: filters.person, currentName: personName }), filters.person);
    filters.floorId = fillSelect(floorSel, floorChoices(office()), filters.floorId);
    filters.deptId = fillSelect(deptSel, deptChoices(office(), filters.floorId), filters.deptId);
    deptSel.disabled = !filters.floorId;
    // A remembered « Discussions » stays until the server answers (load() resets it if refused).
    filters.kind = fillSelect(kindSel, kindChoices(chats || (!chatsKnown && filters.kind === 'chat')), filters.kind);
  }

  function renderTip() {
    if (tipFor === chats) return;
    tipFor = chats;
    tipSlot.replaceChildren(infoTip(t(chats ? 'adm.activity.tipChats' : 'adm.activity.tip'), { label: t('adm.activity.tipAria') }));
  }

  /**
   * Loads the first page (filters changed, refresh, live update) or the next one (`more`). A newer
   * request makes an older answer stale; `quiet` keeps what is shown when it fails. A next page
   * only continues the list shown: not while a first page is due or on its way; a list loaded with
   * other filters than the current ones gets the first page instead.
   */
  async function load({ more = false, quiet = false } = {}) {
    if (destroyed) return;
    const key = filterKey();
    if (more && (!nextBefore || loadingMore || filterTimer || firstPending)) return;
    // The list shown is for other filters (its first page failed quietly): load the first page.
    if (more && key !== itemsKey) more = false;
    const my = ++seq;
    const query = activityQuery(filters, { before: more ? nextBefore : null });
    if (more) {
      loadingMore = true;
      moreBtn.disabled = true;
    } else {
      clearTimeout(filterTimer);
      filterTimer = null;
      firstPending = true;
      moreBtn.disabled = true;
      refreshBtn.disabled = true;
      body.setAttribute('aria-busy', 'true');
    }
    let failed = null;
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/admin/activity', query));
      if (destroyed || my !== seq) return;
      const page = normalizeActivity(res);
      chats = page.chats;
      chatsKnown = true;
      items = more ? mergeItems(items, page.items) : page.items;
      if (!more) itemsKey = key;
      nextBefore = page.nextBefore;
      pages = more ? pages + 1 : 1;
      error = null;
    } catch (err) {
      if (destroyed || my !== seq) return;
      failed = err;
    } finally {
      if (!destroyed && my === seq) {
        if (!more) firstPending = false;
        loadingMore = false;
        moreBtn.disabled = firstPending;
        refreshBtn.disabled = false;
        body.removeAttribute('aria-busy');
      }
    }
    if (failed) {
      if (more) { ctx.toast?.error?.(failed); return; }
      if (quiet && items) return;
      error = failed;
      items = null;
      nextBefore = null;
    }
    // « Discussions » chosen while the server no longer lets administrators read them: everything.
    if (!failed && filters.kind === 'chat' && !chats) {
      filters.kind = '';
      remember();
      load();
      return;
    }
    render();
  }

  async function loadUsers() {
    try {
      const res = await ctx.api.get('/api/users');
      if (destroyed) return;
      users = Array.isArray(res) ? res : Array.isArray(res?.users) ? res.users : [];
    } catch {
      if (destroyed) return;
      users = [];
    }
    renderFilters();
  }

  function render() {
    if (destroyed) return;
    renderFilters();
    renderTip();
    if (!items) {
      head.setCount('');
      if (error) {
        const missing = error.status === 404;
        filterBar.hidden = missing;
        refreshBtn.disabled = missing;
        body.replaceChildren(errorState(error, {
          onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.activity.unavailable'),
        }));
      }
      return;
    }
    filterBar.hidden = false;
    head.setCount(nextBefore ? t('adm.activity.countMore', { n: items.length }) : t('adm.activity.count', { n: items.length }));
    if (!items.length) {
      body.replaceChildren(emptyState({ icon: 'search', title: t('adm.activity.empty') }));
      return;
    }
    const now = Date.now();
    body.replaceChildren(...groupByDay(items, now).map((g) => h('section', { class: 'adm-act-day' },
      h('h4', { class: 'adm-act-day-title mono-label' }, g.label),
      h('ul', { class: 'adm-act-list' }, g.items.map((it) => row(it, now))))),
    ...(nextBefore ? [moreBtn] : []));
  }

  function row(it, now) {
    const name = it.userName || t('adm.activity.someone');
    return h('li', { class: ['adm-act-row', `is-${it.kind}`], dataset: { id: it.id, kind: it.kind } },
      h('span', { class: 'adm-avatar adm-act-avatar', 'aria-hidden': 'true', style: { '--hue': String(hashHue(it.userId || name)) } }, initials(name)),
      h('div', { class: 'adm-act-main' },
        h('div', { class: 'adm-act-meta' },
          h('span', { class: 'adm-act-who' }, name,
            it.role === 'admin' ? badge(t('adm.activity.admin'), 'dark', { class: 'adm-act-role' }) : null,
            it.role === null ? h('span', { class: 'adm-act-gone' }, t('adm.activity.gone')) : null),
          h('span', { class: 'adm-act-team' }, teamLabel(it, office())),
          it.agentName ? h('span', { class: 'adm-act-agent' }, it.agentName) : null,
          timeEl(it.at, now)),
        content(it)));
  }

  function content(it) {
    if (it.kind === 'task') {
      const title = it.title || t('adm.activity.untitled');
      const from = fromLabel(it.from);
      return h('button', {
        type: 'button', class: 'adm-act-content adm-act-open', title: t('adm.activity.openTask', { title }),
        onClick: () => ctx.navigate(`#/task/${encodeURIComponent(it.taskId)}?from=activite`, { replace: true }),
      },
      h('span', { class: 'adm-act-line' }, h('span', { class: 'adm-act-title' }, title), knownState(it.state) ? stateBadge(it.state) : null),
      it.preview ? h('span', { class: 'adm-act-preview' }, it.preview) : null,
      from ? h('span', { class: 'adm-act-from mono-label' }, from) : null);
    }
    if (it.kind === 'chat') {
      const name = it.userName || t('adm.activity.someone');
      return h('button', {
        type: 'button', class: 'adm-act-content adm-act-open', title: t('adm.activity.openChat', { name, agent: it.agentName || t('adm.activity.goneAgent') }),
        onClick: () => openChatViewer(ctx, { ...it.conversation, userName: it.userName, agentName: it.agentName || '' }),
      },
      h('span', { class: 'adm-act-line' }, h('span', { class: 'adm-act-title' }, chatTitle(it)),
        it.files ? h('span', { class: 'adm-act-files', title: t('adm.activity.files', { n: it.files }) },
          icon('image', { size: 14 }), h('span', { 'aria-hidden': 'true' }, String(it.files)),
          h('span', { class: 'sr-only' }, t('adm.activity.files', { n: it.files }))) : null),
      it.preview ? h('span', { class: 'adm-act-preview' }, it.preview) : null);
    }
    const details = actionDetails(it);
    return h('div', { class: 'adm-act-content' },
      h('span', { class: 'adm-act-line' }, badge(auditActionLabel(it.action), auditTone(it.action, it.details), { title: it.action || undefined })),
      details ? h('span', { class: 'adm-act-preview' }, details) : null);
  }

  /** Opens the tab on one person (from the users tab), whatever the floor chosen before. */
  function applyParams(p) {
    if (!p?.userId) return;
    filters = { ...filters, person: String(p.userId), floorId: '', deptId: '' };
    personName = String(p.userName || '');
    remember();
    renderFilters();
    load();
  }

  /** Task events: the first page follows (later pages stay put while someone reads or asks them). */
  function onTask() {
    if (destroyed || !items || pages > 1 || loadingMore || filters.kind === 'chat') return;
    clearTimeout(liveTimer);
    liveTimer = setTimeout(() => {
      liveTimer = null;
      if (destroyed || pages > 1 || loadingMore || el.parentNode?.hidden) return;
      load({ quiet: true });
    }, ACTIVITY_LIVE_MS);
  }

  const offs = [
    ctx.events.on('task', onTask),
    // Floors and services renamed, added or removed: the filters and the team names follow.
    ctx.store.on('office', () => {
      const before = `${filters.floorId}/${filters.deptId}`;
      filters = cleanFilters(filters, office());
      if (`${filters.floorId}/${filters.deptId}` !== before) { remember(); load(); return; }
      if (items) render(); else renderFilters();
    }),
  ];

  // Relative times (« il y a 5 min ») drift: only the <time> texts change every minute.
  const timer = setInterval(() => {
    if (destroyed || !items) return;
    const now = Date.now();
    for (const tm of body.querySelectorAll('time')) tm.textContent = relativeTime(tm.getAttribute('datetime'), now);
  }, 60_000);

  renderFilters();
  renderTip();
  remember();
  loadUsers();
  load();
  return {
    el,
    reload: () => load(),
    applyParams,
    destroy() {
      destroyed = true;
      clearTimeout(filterTimer);
      clearTimeout(liveTimer);
      clearInterval(timer);
      for (const off of offs) off?.();
    },
  };
}

/**
 * The read-only discussion of a person with an agent (GET
 * /api/admin/activity/chats/:userId/:agentId): the bubbles as the person sees them, hers labelled
 * with her name, her images through the administrators' route. No composer, nothing to clear, no
 * task to launch. A 404 (the server does not let administrators read discussions, or the
 * discussion is gone) says so.
 * @param {object} ctx app context
 * @param {{userId: string, agentId: string, userName?: string, agentName?: string}} conv
 * @returns {import('../overlay.js').Layer}
 */
export function openChatViewer(ctx, { userId, agentId, userName = '', agentName = '' } = {}) {
  let conv = { userId: String(userId || ''), agentId: String(agentId || ''), userName, agentName, messages: [] };
  let closed = false;
  const name = () => conv.userName || t('adm.activity.someone');
  const agentLabel = () => conv.agentName || t('adm.activity.goneAgent');
  const noteText = h('span', {});
  const note = h('p', { class: 'adm-act-chat-note', role: 'note' }, icon('lock', { size: 14 }), noteText);
  const state = h('div', { class: 'adm-act-chat-state' }, loadingState());
  const log = h('div', { class: 'ad-chat-log adm-act-chat-log', role: 'log', 'aria-label': t('adm.activity.chat.logLabel') });
  const content = h('div', { class: 'ad-chat adm-act-chat' }, note, state, log);
  const layer = ctx.overlay.openDrawer({
    eyebrow: t('adm.activity.chat.eyebrow'), title: t('adm.activity.chat.title', { name: name(), agent: agentLabel() }),
    size: 'md', className: 'adm-act-chat-drawer', content,
  });
  layer.onClose(() => { closed = true; });

  function syncHead() {
    layer.setTitle(t('adm.activity.chat.title', { name: name(), agent: agentLabel() }));
    const team = conv.floorId ? teamLabel(conv, ctx.store.get().office || {}) : '';
    layer.setSubtitle?.(team && team !== t('adm.activity.noTeam') ? team : '');
    noteText.textContent = t('adm.activity.chat.readOnly', { name: name(), agent: agentLabel() });
  }

  function images(m) {
    const list = (Array.isArray(m.files) ? m.files : []).map((f) => ({ name: f.name, url: adminChatFileUrl(conv.userId, conv.agentId, f.name) }));
    if (!list.length) return null;
    return h('div', { class: 'ad-bubble-images' }, list.map((f, i) => h('button', {
      type: 'button', class: 'ad-bubble-img', 'aria-label': t('adm.activity.chat.openImage', { name: f.name }), title: f.name,
      onClick: () => openLightbox(ctx, list, i),
    }, thumb(f, 'ad-bubble-pic'))));
  }

  function bubble(m, agent) {
    const mine = m.role === 'user';
    const at = toDate(m.at);
    const pics = mine ? images(m) : null;
    return h('div', { class: ['ad-row', mine ? 'is-user' : 'is-agent'] },
      mine ? null : agentAvatar(agent, { size: 28 }),
      h('div', { class: 'ad-bubble-wrap' },
        h('div', { class: ['ad-bubble', mine ? 'is-user' : 'is-agent', { 'has-images': !!pics }] },
          pics,
          mine ? (m.text ? h('p', { class: 'ad-bubble-text' }, m.text) : null) : h('div', { class: 'md ad-bubble-md' }, renderMarkdown(m.text))),
        h('div', { class: 'ad-bubble-meta' },
          h('span', { class: 'ad-bubble-who' }, mine ? name() : agentLabel()),
          at ? h('time', { class: 'ad-bubble-time', datetime: at.toISOString(), title: formatDateTime(at) }, relativeTime(at, Date.now())) : null)));
  }

  function render() {
    syncHead();
    const agent = (ctx.store.get().office?.agents || []).find((a) => a.id === conv.agentId) || { id: conv.agentId, name: agentLabel() };
    log.replaceChildren(...conv.messages.map((m) => bubble(m, agent)));
    state.replaceChildren(...(conv.messages.length ? [] : [emptyState({ icon: 'send', title: t('adm.activity.chat.empty') })]));
  }

  async function load() {
    state.replaceChildren(loadingState());
    try {
      const res = await ctx.api.get(adminChatPath(conv.userId, conv.agentId));
      if (closed) return;
      conv = normalizeConversation(res, conv);
      render();
    } catch (err) {
      if (closed) return;
      syncHead();
      state.replaceChildren(err?.status === 404
        ? emptyState({ icon: 'lock', title: t('adm.activity.chat.notFound'), text: t('adm.activity.chat.notFoundText') })
        : errorState(err, { onRetry: () => load() }));
    }
  }

  syncHead();
  load();
  return layer;
}
